import {
  Alert,
  AlertTitle,
  GridLegacy,
  ListItemIcon,
  ListSubheader,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  gridSpacing,
  infoSectionHeroSx,
  infoSectionRowBorderSx,
} from 'constants/themeConstants';
import Hero from 'ui-component/Hero';
import { useUpdateKitRequestsMutation } from 'store/slices/clientV2/kitRequests';
import { calculateKitRequestStatus } from 'views/kitting/Kit/utils';
import InfoSectionItem, {
  InfoSectionItemFormatTypes,
} from 'ui-component/InfoSectionItem';
import { LabelFormat, StockLocationType } from 'types/inventory';
import EditInline from 'ui-component/EditInline';
import HookFormStockLocationSelect from 'ui-component/HookFormComponents/HookFormStockLocationSelect';
import { Control, FieldErrors } from 'react-hook-form';
import { createUpdateKitRequestFormConstants } from 'views/kitting/components/constants';
import { skipToken } from '@reduxjs/toolkit/dist/query/react';
import _ from 'lodash';
import AddressCard from 'ui-component/cards/AddressCard';
import LabelPreview from 'ui-component/shared/LabelPreview';
import { useDefaultLabelTemplates } from 'hooks/useDefaultLabelTemplates';
import {
  getDefaultShippingMethod,
  getPickupOptions,
  getShipmentCarrierAndService,
} from 'utils/functions';
import {
  HookFormDatePicker,
  HookFormInput,
  HookFormSelect,
} from 'ui-component/HookFormComponents';
import { ShippingMethods } from 'types/purchasing';
import { IconPlaneDeparture } from '@tabler/icons-react';
import { useSelector } from 'store';
import { useGetOrgQuery } from 'store/slices/apiV1/org';
import { useIsCofactr } from 'hooks/useIsCofactr';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import { ALL_APP_IDS } from 'constants/appConstants';
import {
  KitRequestSubtype,
  useKitRequestContext,
} from 'views/kitting/Kit/KitRequestContext';
import DetailCustomProperties from 'ui-component/DetailCustomProperties';
import { CustomPropertyModel } from 'types/customProperty';
import { KitRequestSchema } from 'types/kitting';
import { useDialogManager } from 'hooks/useDialogManager';
import EstimatedShipDateDialog from 'views/kitting/Kit/components/EstimatedShipDateDialog';
import { EstimatedShipDateAlert } from 'views/kitting/Kit/components/EstimatedShipDateAlert';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';
import { convertDate, formatToUniversalDate } from 'utils/dates';
import { approveUnapproveKitRequestValidationSchema } from 'views/kitting/Kit/components/ApproveUnapproveKitRequestDialog';
import { HookFormQuillInput } from 'ui-component/HookFormComponents/HookFormQuillInput';
import { useMemo } from 'react';

enum KIT_DETAIL_DIALOGS {
  UPDATE_SHIP_DATE = 'UPDATE_SHIP_DATE',
}

const KitDetail = ({ kitRequestId }: { kitRequestId: string }) => {
  const tempKitEstimates = useFeature(FeatureFlags.tempKitEstimates).on;
  const { isCofactr } = useIsCofactr();
  const { hasAppPermission } = useAppAccessContext();
  const { openDialog, closeDialog, isDialogOpen } = useDialogManager(
    Object.values(KIT_DETAIL_DIALOGS)
  );

  const { activeOrgId } = useSelector((state) => state.org);
  const { data: org } = useGetOrgQuery(activeOrgId ?? skipToken);

  const {
    kitRequest,
    isLoadingKitRequest,
    isSuccessKitRequest,
    isFetchingKitRequest,
    estimatedShipDateData,
    isLoadingKitRequestLines,
  } = useKitRequestContext();

  const { fetching: isFetchingLabelTemplates, getLabelTemplate } =
    useDefaultLabelTemplates({ shipToId: kitRequest?.shipTo?.id });

  const dynamicShippingMethods = useMemo(() => {
    const pickupOptions = getPickupOptions(
      hasAppPermission(ALL_APP_IDS.WAREHOUSE)
    );

    return [...ShippingMethods, ...pickupOptions];
  }, [hasAppPermission]);

  const disableEdit = Boolean(kitRequest?.locked);

  if (isSuccessKitRequest && !isFetchingKitRequest && !kitRequest) {
    return (
      <Alert severity="warning">
        <AlertTitle>Kit not found</AlertTitle>
        This kit may have been deleted or is not currently accessible. Please
        contact Cofactr Support if you believe this is an error.
      </Alert>
    );
  }

  return (
    <GridLegacy container>
      <GridLegacy item container xs={12} sx={infoSectionHeroSx}>
        <GridLegacy item xs={6}>
          <Hero
            usePizzaTracker
            isLoading={isLoadingKitRequest}
            options={{
              top: {
                value: kitRequest?.kitCode ?? '',
                clipboard: 'Kit Code',
                clipboardValue: kitRequest?.kitCode ?? '',
              },
              middle: {
                value: kitRequest?.name ?? '',
                clipboard: 'Kit Name',
                clipboardValue: kitRequest?.name ?? '',
                editInline: (
                  <EditInline
                    useMutation={useUpdateKitRequestsMutation}
                    name={createUpdateKitRequestFormConstants.name.id}
                    label={createUpdateKitRequestFormConstants.name.label}
                    value={kitRequest?.name}
                    id={kitRequestId}
                    submitAsClientV2Api
                  >
                    <HookFormInput disableRequiredProps type="text" />
                  </EditInline>
                ),
              },
              status: kitRequest ? calculateKitRequestStatus(kitRequest) : '',
            }}
          />
        </GridLegacy>
      </GridLegacy>
      <GridLegacy
        item
        container
        sx={infoSectionRowBorderSx}
        justifyContent="space-between"
        spacing={gridSpacing}
      >
        <GridLegacy item xs={4}>
          <InfoSectionItem
            caption={createUpdateKitRequestFormConstants.sourceLocations.label}
            isLoading={isLoadingKitRequest}
            formatType={InfoSectionItemFormatTypes.children}
            value
            disableEdit={disableEdit}
            editInline={
              <EditInline
                useMutation={useUpdateKitRequestsMutation}
                name={createUpdateKitRequestFormConstants.sourceLocations.id}
                label={
                  createUpdateKitRequestFormConstants.sourceLocations.label
                }
                hideLabel
                value={_.map(kitRequest?.sourceLocations, 'id')}
                id={kitRequestId}
                submitAsClientV2Api
              >
                <HookFormStockLocationSelect
                  filters={[
                    {
                      field: 'locationType',
                      operator: '=',
                      value: StockLocationType.FACILITY,
                    },
                    {
                      field: 'warehouseShippingEnabled',
                      operator: '=',
                      value: true,
                    },
                  ]}
                  defaultValue={_.map(kitRequest?.sourceLocations, 'id')}
                  disableRequiredProps
                  errors={{} as FieldErrors}
                  control={{} as Control}
                  multiple
                  name=""
                  label=""
                />
              </EditInline>
            }
          >
            <Stack spacing={1}>
              {_.map(kitRequest?.sourceLocations, (location) => (
                <AddressCard
                  location={location}
                  key={location.id}
                  startCollapsed
                />
              ))}
            </Stack>
          </InfoSectionItem>
        </GridLegacy>
        <GridLegacy item xs={4}>
          <InfoSectionItem
            caption={createUpdateKitRequestFormConstants.shipTo.label}
            isLoading={isLoadingKitRequest}
            formatType={InfoSectionItemFormatTypes.stockLocationAddress}
            value={Boolean(kitRequest?.shipTo)}
            stockLocationValue={kitRequest?.shipTo}
            color={!kitRequest?.shipTo ? 'warning' : undefined}
            placeholder="Kit will not ship until a destination facility is set"
            tooltip={
              !kitRequest?.shipTo
                ? 'Click the edit button to set a destination facility'
                : ''
            }
            disableEdit={disableEdit}
            editInline={
              <EditInline
                useMutation={useUpdateKitRequestsMutation}
                name={createUpdateKitRequestFormConstants.shipTo.id}
                label={createUpdateKitRequestFormConstants.shipTo.label}
                hideLabel
                value={kitRequest?.shipTo?.id}
                id={kitRequestId}
                submitAsClientV2Api
              >
                <HookFormStockLocationSelect
                  filters={[
                    {
                      field: 'locationType',
                      operator: '=',
                      value: StockLocationType.FACILITY,
                    },
                  ]}
                  defaultValue={kitRequest?.shipTo?.id}
                  disableRequiredProps
                  errors={{} as FieldErrors}
                  control={{} as Control}
                  name=""
                  label=""
                />
              </EditInline>
            }
          />
        </GridLegacy>
        {kitRequest?.shipTo && (
          <GridLegacy item xs={4}>
            <InfoSectionItem
              caption="Kit Label Preview"
              isLoading={isLoadingKitRequest}
              clipboard={false}
              formatType={InfoSectionItemFormatTypes.children}
              value
            >
              <LabelPreview
                labelTemplate={getLabelTemplate(LabelFormat.KitRequestDefault)}
                loading={isFetchingLabelTemplates}
                kitRequest={kitRequestId}
                size="small"
              />
            </InfoSectionItem>
          </GridLegacy>
        )}
      </GridLegacy>
      <GridLegacy
        item
        container
        sx={infoSectionRowBorderSx}
        justifyContent="space-between"
        spacing={gridSpacing}
      >
        {kitRequest?.shipTo && (
          <InfoSectionItem
            xs={4}
            caption="Shipping Method"
            isLoading={isLoadingKitRequest}
            value={
              kitRequest?.carrier && kitRequest?.service
                ? `${kitRequest?.carrier}: ${kitRequest?.service}`
                : null
            }
            color={
              !kitRequest?.carrier || !kitRequest?.service
                ? 'warning'
                : undefined
            }
            placeholder="Kit will not ship until a shipping method is set"
            tooltip={
              !kitRequest?.carrier || !kitRequest?.service
                ? 'Click the edit button to set a shipping method'
                : ''
            }
            disableEdit={disableEdit || !kitRequest?.shipTo}
            editInline={
              <EditInline
                useMutation={useUpdateKitRequestsMutation}
                name="method"
                id={kitRequestId}
                label="Shipping Method"
                value={
                  kitRequest?.carrier && kitRequest?.service
                    ? `${kitRequest.carrier}_${kitRequest.service}`
                    : getDefaultShippingMethod({ org })
                }
                preSubmit={(value: string | null) => {
                  if (value) {
                    const { defaultShippingCarrier, defaultShippingService } =
                      getShipmentCarrierAndService(value);
                    return {
                      carrier: defaultShippingCarrier,
                      service: defaultShippingService,
                    } as unknown as {
                      carrier: string;
                      service: string;
                    };
                  }
                  return null;
                }}
                submitAsClientV2Api
              >
                <HookFormSelect disableRequiredProps>
                  {dynamicShippingMethods.map((method) =>
                    method.type === 'option' ? (
                      <MenuItem value={method.value} key={method.value}>
                        <Stack
                          direction="row"
                          justifyContent="flex-start"
                          alignItems="center"
                        >
                          {method.value ===
                            getDefaultShippingMethod({ org }) && (
                            <Tooltip title="The default shipping method for your organization">
                              <ListItemIcon>
                                <IconPlaneDeparture />
                              </ListItemIcon>
                            </Tooltip>
                          )}
                          <Typography>{method.label}</Typography>
                        </Stack>
                      </MenuItem>
                    ) : (
                      <ListSubheader key={method.value}>
                        {method.label}
                      </ListSubheader>
                    )
                  )}
                </HookFormSelect>
              </EditInline>
            }
          />
        )}
        <InfoSectionItem
          xs={4}
          caption={createUpdateKitRequestFormConstants.externalNotes.label}
          isLoading={isLoadingKitRequest}
          value={kitRequest?.externalNotes}
          disableEdit={disableEdit}
          formatType={InfoSectionItemFormatTypes.richText}
          editInline={
            <EditInline
              useMutation={useUpdateKitRequestsMutation}
              name={createUpdateKitRequestFormConstants.externalNotes.id}
              hideLabel
              value={kitRequest?.externalNotes}
              id={kitRequestId}
              submitAsClientV2Api
              fullHeight
            >
              <HookFormQuillInput disableRequiredProps hideLabel />
            </EditInline>
          }
        />
        {tempKitEstimates && (
          <InfoSectionItem
            xs={4}
            caption="Estimated Ship Date"
            isLoading={isLoadingKitRequest}
            formatType={InfoSectionItemFormatTypes.dateShort}
            value={estimatedShipDateData.estimatedShipDateToRender}
            disableEdit={disableEdit || !kitRequest?.approved}
            editInline={
              <EditInline
                useMutation={useUpdateKitRequestsMutation}
                name="estimatedShipDate"
                label="Estimated Ship Date"
                hideLabel
                value={
                  estimatedShipDateData.estimatedShipDateToRender
                    ? convertDate(
                        estimatedShipDateData.estimatedShipDateToRender
                      )
                    : null
                }
                id={kitRequestId}
                submitAsClientV2Api
                validation={
                  approveUnapproveKitRequestValidationSchema.fields
                    .targetShipDate
                }
                preSubmit={(value: string | null) =>
                  value ? formatToUniversalDate(new Date(value)) : null
                }
              >
                <HookFormDatePicker
                  disableRequiredProps
                  minDate={
                    estimatedShipDateData.estimatedShipDate
                      ? convertDate(estimatedShipDateData.estimatedShipDate)
                      : undefined
                  }
                />
              </EditInline>
            }
          />
        )}
        {!tempKitEstimates && (
          <InfoSectionItem
            xs={4}
            caption="Target Ship Date"
            isLoading={isLoadingKitRequest}
            value={kitRequest?.targetShipDate}
            formatType={InfoSectionItemFormatTypes.dateShort}
            placeholder="ASAP"
            disableEdit={disableEdit}
            editInline={
              <EditInline
                useMutation={useUpdateKitRequestsMutation}
                name="targetShipDate"
                label="Target Ship Date"
                hideLabel
                value={
                  kitRequest?.targetShipDate
                    ? convertDate(kitRequest?.targetShipDate)
                    : null
                }
                id={kitRequestId}
                submitAsClientV2Api
                preSubmit={(value: string | null) =>
                  value ? formatToUniversalDate(new Date(value)) : null
                }
              >
                <HookFormDatePicker disableRequiredProps disablePast />
              </EditInline>
            }
          />
        )}
      </GridLegacy>
      <GridLegacy
        item
        container
        sx={infoSectionRowBorderSx}
        justifyContent="space-between"
        spacing={gridSpacing}
      >
        <InfoSectionItem
          xs={4}
          caption="Printed Notes"
          isLoading={isLoadingKitRequest}
          value={kitRequest?.printedNotes}
          disableEdit={disableEdit}
          editInline={
            <EditInline
              useMutation={useUpdateKitRequestsMutation}
              name="printedNotes"
              label="Printed Notes"
              hideLabel
              value={kitRequest?.printedNotes}
              id={kitRequestId}
              submitAsClientV2Api
            >
              <HookFormInput disableRequiredProps />
            </EditInline>
          }
        />
        {isCofactr && (
          <InfoSectionItem
            caption={createUpdateKitRequestFormConstants.internalNotes.label}
            isLoading={isLoadingKitRequest}
            value={kitRequest?.internalNotes}
            disableEdit={disableEdit}
            editInline={
              <EditInline
                useMutation={useUpdateKitRequestsMutation}
                name={createUpdateKitRequestFormConstants.internalNotes.id}
                label={createUpdateKitRequestFormConstants.internalNotes.label}
                hideLabel
                value={kitRequest?.internalNotes}
                id={kitRequestId}
                submitAsClientV2Api
              >
                <HookFormInput disableRequiredProps />
              </EditInline>
            }
          />
        )}
      </GridLegacy>
      {kitRequest && (
        <DetailCustomProperties<KitRequestSchema, KitRequestSubtype>
          useMutation={useUpdateKitRequestsMutation}
          record={kitRequest}
          model={CustomPropertyModel.KIT_REQUEST}
          isLoadingRecord={isLoadingKitRequest}
          disableEdit={disableEdit}
          idProp="id"
          submitAsClientV2Api
        />
      )}
      {tempKitEstimates && (
        <>
          {isDialogOpen(KIT_DETAIL_DIALOGS.UPDATE_SHIP_DATE) && kitRequest && (
            <EstimatedShipDateDialog
              open={isDialogOpen(KIT_DETAIL_DIALOGS.UPDATE_SHIP_DATE)}
              onClose={closeDialog}
              kitRequest={kitRequest}
              estimatedShipDateData={estimatedShipDateData}
            />
          )}
          {!isLoadingKitRequest &&
            !isLoadingKitRequestLines &&
            estimatedShipDateData.hasEstimatedShipDateChanged && (
              <GridLegacy item xs={12} sx={{ mb: 2 }}>
                <EstimatedShipDateAlert
                  estimatedShipDateData={estimatedShipDateData}
                  savedShipDate={kitRequest?.estimatedShipDate ?? null}
                  shipPartial={kitRequest?.shipPartial ?? false}
                  onClick={() =>
                    openDialog(KIT_DETAIL_DIALOGS.UPDATE_SHIP_DATE)
                  }
                />
              </GridLegacy>
            )}
        </>
      )}
    </GridLegacy>
  );
};

export default KitDetail;
